import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import FileCopy from '@material-ui/icons/FileCopy';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/root';
import { dispatch } from '../../../redux/store';
import { setKeyId, setShowConfirmDelete } from '../../../redux/slices/wikiKeysSlice';
import { useSearchWikiKeysWithClientsQuery } from '../../../redux/rtkQuery/apiWikiKeysSlice';
import UrlLink from '../../clientModule/components/Common/Table/UrlLink';
import { copyToClipboard } from '../../../utils';
import { sanitizeHtml, htmlToPlainText } from '../../../utils/sanitizeHtml';
import { SearchBar, ModalDefaultDraggable } from '../../../molecules';
import { Loader } from '../../../atoms';
import ConfirmDeletePopup from './ConfirmDeletePopup';
import AddCredentialModal from './AddCredentialModal';
import EditCredentialModal from './EditCredentialModal';

const Keys = () => {
  const [searchText, setSearchText] = useState('');
  const [credentialRow, setCredentialRow] = useState<any>(null);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);
  // EA-1200: notes are shown behind a "View" link (like Fee Comments) so long
  // notes don't expand the row. This holds the row whose notes are open.
  const [viewNotesRow, setViewNotesRow] = useState<any>(null);
  const [jurisdictionFilter, setJurisdictionFilter] = useState('');
  const { showConfirmDelete } = useSelector((s: RootState) => s.wikiKeysSlice);

  const shouldSearch = searchText.length >= 3;
  const { data, isFetching } = useSearchWikiKeysWithClientsQuery({ search: searchText }, { skip: !shouldSearch });

  const allRows = shouldSearch ? data || [] : [];

  // Build unique jurisdiction list from current results
  const jurisdictionOptions = allRows
    .map((r: any) => r.jurisdictionName)
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort();

  // Apply jurisdiction filter
  const rows = jurisdictionFilter
    ? allRows.filter((r: any) => r.jurisdictionName === jurisdictionFilter)
    : allRows;

  const onSearch = useCallback((value: string) => {
    setSearchText(value);
    setJurisdictionFilter('');
  }, []);

  const handleDelete = (id: string) => {
    dispatch(setKeyId(id));
    dispatch(setShowConfirmDelete(true));
  };

  // Notes are authored as rich text, so the cell renders the real markup
  // (bullets, bold, colour) rather than a flattened string - block elements
  // collapsed into each other when they were stripped. htmlToPlainText only
  // decides whether there is anything to show at all.
  const hasNotes = (notes?: string): boolean => htmlToPlainText(notes).length > 0;

  const getClientName = (row: any): string => {
    const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
    return [name, row.companyName].filter(Boolean).join(' - ') || '';
  };

  return (
    <Wrapper>
      <MainArea>
        <SearchRow>
          <SearchLabel>Search:</SearchLabel>
          <SearchBarWrapper>
            <SearchBar onChange={onSearch} text={searchText} debounceDelay={400} />
          </SearchBarWrapper>
          <FilterSelect value={jurisdictionFilter} onChange={e => setJurisdictionFilter(e.target.value)}>
            <option value="">All Jurisdictions</option>
            {jurisdictionOptions.map((name: string) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </FilterSelect>
          <AddCredentialButton onClick={() => setShowAddCredential(true)}>Add Credential</AddCredentialButton>
        </SearchRow>
        <TableWrapper>
          <StyledTable>
            <thead>
              <HeaderRow>
                <Th>Client</Th>
                <Th>Email</Th>
                <Th>Jurisdiction</Th>
                <Th>Portal Link</Th>
                <Th>Login</Th>
                <Th>Key</Th>
                <Th>Notes</Th>
                <ThActions>Actions</ThActions>
              </HeaderRow>
            </thead>
            <tbody>
              {isFetching ? (
                <tr>
                  <EmptyTd colSpan={8}>
                    <LoadingWrapper>
                      <Loader size={24} />
                      <span>Loading...</span>
                    </LoadingWrapper>
                  </EmptyTd>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <EmptyTd colSpan={8}>
                    {shouldSearch ? 'No results found' : 'Type at least 3 characters to start searching'}
                  </EmptyTd>
                </tr>
              ) : (
                rows.map((row: any, index: number) => (
                  <BodyRow key={`${row.userId}-${row.keyId || index}`}>
                    <Td>{getClientName(row)}</Td>
                    <Td>{row.email || ''}</Td>
                    <Td>{row.jurisdictionName || ''}</Td>
                    <Td>
                      {row.portalLink ? (
                        <UrlLink value={row.portalLink} caption={row.portalLinkDescription || row.portalLink} />
                      ) : (
                        <NoValue>-</NoValue>
                      )}
                    </Td>
                    <TdWithIcon>
                      {row.username && (
                        <CopyIcon onClick={() => copyToClipboard(row.username)} />
                      )}
                      <CellText>{row.username || ''}</CellText>
                    </TdWithIcon>
                    <TdWithIcon>
                      {row.password && (
                        <CopyIcon onClick={() => copyToClipboard(row.password)} />
                      )}
                      <CellText>{row.password ? '******' : ''}</CellText>
                    </TdWithIcon>
                    <TdNotes>
                      {hasNotes(row.notes) ? (
                        <NotesViewLink
                          type="button"
                          onClick={() => setViewNotesRow(row)}
                        >
                          View
                        </NotesViewLink>
                      ) : (
                        <NoValue>-</NoValue>
                      )}
                    </TdNotes>
                    <TdActions>
                      <ActionIcon title="Add Credential" onClick={() => setCredentialRow(row)}>
                        <AddCircleOutlineIcon fontSize="small" />
                      </ActionIcon>
                      {row.keyId && (
                        <ActionIcon title="Edit" onClick={() => setEditRow(row)}>
                          <EditIcon fontSize="small" />
                        </ActionIcon>
                      )}
                      {row.keyId && (
                        <ActionIcon title="Delete" onClick={() => handleDelete(row.keyId)}>
                          <DeleteIcon fontSize="small" />
                        </ActionIcon>
                      )}
                    </TdActions>
                  </BodyRow>
                ))
              )}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </MainArea>

      {credentialRow && (
        <AddCredentialModal row={credentialRow} onClose={() => setCredentialRow(null)} />
      )}

      {showAddCredential && (
        <AddCredentialModal onClose={() => setShowAddCredential(false)} />
      )}

      {editRow && (
        <EditCredentialModal row={editRow} onClose={() => setEditRow(null)} />
      )}

      {viewNotesRow && (
        <ModalDefaultDraggable
          title={`Notes for ${getClientName(viewNotesRow)}`}
          onClose={() => setViewNotesRow(null)}
        >
          <NotesModalBody
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewNotesRow.notes) }}
          />
        </ModalDefaultDraggable>
      )}

      {showConfirmDelete && <ConfirmDeletePopup />}
    </Wrapper>
  );
};

export default Keys;

const Wrapper = styled.div`
  width: 100%;
  flex: 1;
`;

const MainArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const SearchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  margin: 0 0 16px;
  padding: 16px 20px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
`;

const SearchLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-600);
  white-space: nowrap;
`;

const SearchBarWrapper = styled.div`
  flex: 1;
  min-width: 240px;
  max-width: 320px;
`;

const FilterSelect = styled.select`
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  background-color: var(--neutral-0);
  color: var(--mainText);
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const HeaderRow = styled.tr`
  background-color: var(--neutral-100);
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  border-bottom: 1px solid var(--border);
`;

const ThActions = styled(Th)`
  width: 130px;
  text-align: center;
`;

const BodyRow = styled.tr`
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--neutral-50);
  }

  &:last-child td {
    border-bottom: 0;
  }
`;

const Td = styled.td`
  text-align: left;
  padding: 14px 16px;
  font-size: 0.875rem;
  color: var(--mainText);
  border-bottom: 1px solid var(--border);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const EmptyTd = styled.td`
  text-align: center;
  padding: 32px 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
  border-bottom: 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const TdWithIcon = styled(Td)`
  position: relative;
  padding-left: 32px;
`;

const CellText = styled.span`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: calc(100% - 24px);
`;

const CopyIcon = styled(FileCopy)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px !important;
  color: #999 !important;
  cursor: pointer;

  &:hover {
    color: #333 !important;
  }
`;

const TdNotes = styled(Td)`
  white-space: normal;
  overflow: visible;
`;

// EA-1200: "View" hyperlink in the Notes cell (mirrors Fee Comments). Keeps the
// row height fixed no matter how long the notes are.
const NotesViewLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.8125rem;
  color: var(--primary-600);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

/**
 * Rich-text notes rendered inside the "View" popup: keep the author's
 * formatting but strip the browser's default list/paragraph margins so the
 * content reads cleanly.
 */
const NotesModalBody = styled.div`
  padding: 8px 20px 20px;
  min-width: 320px;
  max-width: 520px;
  max-height: 60vh;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.5;
  word-break: break-word;
  text-align: left;

  p,
  h1,
  h2,
  h3,
  h4,
  blockquote,
  pre {
    margin: 0 0 8px;
  }

  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 20px;
  }

  li {
    margin: 0;
  }

  a {
    color: var(--primary-600);
  }
`;

const TdActions = styled(Td)`
  text-align: center;
  width: 130px;
  padding-right: 12px;
`;

const ActionIcon = styled.span`
  display: inline-flex;
  cursor: pointer;
  color: var(--defaultTableCellTitle);
  margin: 0 4px;

  &:hover {
    color: var(--mainText);
  }
`;

const NoValue = styled.span`
  color: var(--defaultTableCellTitle);
`;

const AddCredentialButton = styled.button`
  margin-left: auto;
  min-width: 160px;
  height: 40px;
  padding: 0 20px;
  background-color: var(--primary-600);
  color: var(--neutral-0);
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background-color: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
